// Imagen de respaldo para productos sin foto.
//
// POR QUÉ EXISTE: hasta F8 los 7 componentes que muestran imagen de producto
// tenían cada uno su propio `const PLACEHOLDER = 'https://via.placeholder.com/...'`.
// Ese servicio dejó de responder (censo 24-jul-2026: 62 peticiones fallidas con
// net::ERR_CONNECTION_CLOSED en un solo recorrido de la app). Cada producto sin
// foto disparaba una petición externa que fallaba, en cada render.
//
// DECISIONES:
// - SVG como data URI, no un archivo suelto en /public ni un import de imagen:
//   cero peticiones de red, cero request adicional, cero dependencia de terceros.
// - Los colores salen de los tokens de marca (regla de CLAUDE.md: nunca un hex
//   hardcodeado en un componente). Como un data URI no puede leer `var(--x)`,
//   se arma el string a partir de `tokens.js`, que es el espejo JS de tokens.css.
// - Se genera al tamaño del hueco donde va, para que el ícono quede centrado y a
//   escala en cualquier proporción (hay call sites de 40x40 y de 300x200).
//
// Si algún día se cambia el violeta, esto se actualiza solo vía tokens.js.

import { BRAND } from '../styles/tokens';

const [r, g, b] = BRAND.primaryRgb;

// Ícono "image" de Lucide (viewBox 24), stroke 1.5 — el estándar de íconos del
// proyecto. Se dibuja centrado y ocupando ~38% del lado menor. Se usan los
// elementos tal como los emite Lucide en vez de aplastarlos en un solo `path`:
// el círculo como arco es fácil de escribir mal (un arco que arranca en el
// centro y no en el borde dibuja cualquier cosa).
const GLYPH =
  '<rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>'
  + '<circle cx="9" cy="9" r="2"/>'
  + '<path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>';

const cache = new Map();

/**
 * Data URI de un SVG neutro con el ícono de imagen, en tinte de marca.
 * @param {number} width  ancho del hueco en px
 * @param {number} height alto del hueco en px (por defecto, cuadrado)
 */
export function productPlaceholder(width = 96, height = width) {
  const key = `${width}x${height}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const side = Math.min(width, height);
  const icon = Math.max(16, Math.round(side * 0.38));   // el ícono nunca baja de 16px
  const scale = icon / 24;
  const x = (width - icon) / 2;
  const y = (height - icon) / 2;
  const radius = Math.max(2, Math.round(side * 0.06));

  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
    `<rect width="${width}" height="${height}" rx="${radius}" fill="${BRAND.primaryLight}"/>`,
    `<g transform="translate(${x} ${y}) scale(${scale})" fill="none"`,
    ` stroke="rgba(${r},${g},${b},0.42)" stroke-width="1.5"`,
    ` stroke-linecap="round" stroke-linejoin="round">`,
    GLYPH,
    `</g></svg>`,
  ].join('');

  const uri = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  cache.set(key, uri);
  return uri;
}

// Tamaño genérico, para quien no tenga un hueco definido.
export const PRODUCT_PLACEHOLDER = productPlaceholder();
